import PropTypes from 'prop-types'
import { memo, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useRootContext } from '@/app/services/RootProvider/RootProvider'
import Icon from '@/components/core/Icon/Icon'
import WedgeShape from '@/components/elements/WedgeShape/WedgeShape'
import classNames from '@/lib/util/classNames'
import useOption from '../hooks/useOption'
import * as styles from './Option.module.scss'

/**
 * The `Options
 * @param {object} props - the props
 * @returns {React.ReactElement} the element
 */
function Option (props) {
  const { target, title, url } = props
  const { setIsBurgerMenuOpen } = useRootContext()
  const location = useLocation()
  const isActive = location.pathname === url
  const {
    isHover,
    refBackground,
    refBlend,
    refButton,
    refWedge,
    setIsHover,
    uid
  } = useOption(props)
  const classNameOutput = useMemo(
    () => classNames(styles.container, isHover && styles.hover, isActive && styles.active),
    [isHover, isActive]
  )
  const styleOutput = useMemo(
    () => ({
      '--mask-url': `url("#${uid}")`
    }),
    [uid]
  )

  const handleMouseOver = useCallback(() => {
    setIsHover(true)
  }, [])

  const handleMouseOut = useCallback(() => {
    setIsHover(false)
  }, [])

  const handleClick = useCallback(() => {
    setIsBurgerMenuOpen(false)
  }, [])

  return (
    <div className={classNameOutput} style={styleOutput}>
      <div ref={refBackground} className={styles.background} />
      <div ref={refBlend} className={styles.blend}>
        <div className={styles.blendInner} />
      </div>
      {target === 'self' && (
        <Link
          ref={refButton}
          className={styles.button}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          to={url}
          onClick={handleClick}
        >
          {title}
        </Link>
      )}
      {target !== 'self' && (
        <a
          ref={refButton}
          href={url}
          target="_blank"
          className={styles.button}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onClick={handleClick}
          rel="noreferrer"
        >
          <div className={styles.content}>
            <span>{title}</span>
            <Icon icon="external_link" />
          </div>
        </a>
      )}
      <WedgeShape ref={refWedge} className={styles.wedge} svgId={uid} />
    </div>
  )
}

Option.propTypes = {
  target: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string
}

export default memo(Option)
